import { CalendarClock, CloudRain, Snowflake, Thermometer } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { TripOutlook, UiLang } from "../lib/types";

interface Props {
  outlook: TripOutlook;
  lang: UiLang;
}

/**
 * Hva klimaet sier, når datoen ligger lenger fram enn varselet rekker.
 *
 * ── Hva som sto her før ────────────────────────────────────────────────────
 *
 * «Ingen værdata for disse datoene.» Sant, og ingenting å planlegge etter. En
 * Norgestur planlegges i februar for juli, og MET rekker ni døgn — så for det
 * flertallet av brukere som planlegger mer enn en uke fram, slo appens mest
 * særpregede funksjon seg av og sa fra om det. Utsiktsindeksen sto på den
 * nøytrale 60 for hvert stopp, som ikke er et anslag; det er «vi vet ikke»,
 * skrevet som et tall.
 *
 * ── Hva som står her nå ────────────────────────────────────────────────────
 *
 * Klimanormalen 1991–2020, som allerede var bakt inn per stopp for klesrådet.
 * Den svarer ikke på «hvordan blir været den 14. juli» — ingenting gjør det
 * fem måneder fram — men den svarer på det spørsmålet folk faktisk stiller:
 * «hvordan er juli der, vanligvis?»
 *
 * Panelet er bygget rundt tre ting som må stå sammen for at tallet skal kunne
 * leses riktig:
 *
 *   1. **Hvor langt fram det er, og hvor langt varselet rekker.** Uten det ser
 *      anslaget ut som et varsel.
 *   2. **Datoen varselet begynner å dekke turen.** Det gjør «vi vet ikke ennå»
 *      om fra en blindvei til en avtale man kan sette i kalenderen.
 *   3. **Dekningen.** Alle 118 stopp har normaler nå, og et anslag som
 *      dekker fire av åtte stopp skal si det framfor å gjette på de fire andre.
 */
export function OutlookPanel({ outlook, lang }: Props) {
  const t = dictionaries[lang];

  return (
    <section className="card outlook" aria-labelledby="outlook-title">
      <div className="outlook-head">
        <h3 className="card-title" id="outlook-title">
          <CalendarClock size={15} />
          {t.outlookTitle}
        </h3>
        {/*
          Merket sier at dette er en normal og ikke et varsel, og det står ved
          tallet framfor i teksten under. Et anslag som må leses nedenfra for å
          forstås, blir lest som et varsel.
        */}
        <span className="outlook-badge">{t.outlookEstimate}</span>
      </div>

      <p className="outlook-headline">{outlook.headline}</p>

      <div className="outlook-figures">
        <div className="outlook-figure">
          <b>{outlook.score}</b>
          <span>{t.outlookScore}</span>
          <em>{outlook.label}</em>
        </div>
        <div className="outlook-figure">
          <b>
            <Thermometer size={13} /> {outlook.coldestC}–{outlook.warmestC} °C
          </b>
          <span>{t.outlookRange}</span>
          <em>{t.outlookColdestAt(outlook.coldestPlace)}</em>
        </div>
        <div className="outlook-figure">
          <b>
            <CloudRain size={13} /> {Math.round(outlook.wetShare * 10)}/10
          </b>
          <span>{t.outlookWetDays}</span>
        </div>
        {outlook.snowShare > 0.1 && (
          <div className="outlook-figure">
            <b>
              <Snowflake size={13} /> {Math.round(outlook.snowShare * 100)} %
            </b>
            <span>{t.outlookAsSnow}</span>
          </div>
        )}
      </div>

      <ul className="outlook-notes">
        {outlook.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>

      {/*
        Kildelinja sa «MET varsler 9 døgn fram; lenger enn det finnes ingen
        varsel å regne på — bare klima». Den andre halvdelen sluttet å være
        sann da langtidsvarselet kom til: det finnes et varsel mellom de to
        horisontene. Begge tallene kommer nå fra svaret framfor fra teksten, så
        en flyttet horisont ikke etterlater en påstand som ikke stemmer.
      */}
      <p className="outlook-source">
        {t.outlookSource(
          outlook.covered,
          outlook.total,
          outlook.horizonDays,
          outlook.extendedHorizonDays,
        )}
      </p>
    </section>
  );
}
